import { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@poseidon/firebase/firebase";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";

const toDate = (value) => (value ? new Date(value) : null);

const mapRow = (row) => ({
  ...row,
  startDate: toDate(row.startDate),
  closeDate: toDate(row.closeDate),
  dateOfBirth: toDate(row.dateOfBirth),
  address: [...row.addressLines, row.postcode].filter((l) => !!l).join(", "),
  warningCount: row.warnings.length,
});

/**
 * Fetches a fresh CAIS snapshot from the backend. `refresh` reloads the
 * table; `download` fetches again (so the file is as-at the click) and saves
 * the fixed-width file with Experian's expected filename.
 */
export const useCaisReport = () => {
  // Memoised deliberately: react-firebase-hooks' useHttpsCallable returns a
  // new function every render, which would re-trigger the initial fetch
  // effect below on each state change and call the backend in a loop.
  const executeCallable = useMemo(
    () => httpsCallable(functions, "caisReport"),
    []
  );
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const [report, setReport] = useState();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = useCallback(async () => {
    const result = await executeCallable({});
    if (!result?.data?.success) {
      throw new Error("Failed to generate the CAIS report");
    }
    const data = result.data;
    const mapped = {
      generatedAt: new Date(data.generatedAt),
      rows: data.rows.map(mapRow),
      file: data.file,
    };
    setReport(mapped);
    return mapped;
  }, [executeCallable]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await fetchReport();
    } catch (error) {
      setError(error.message ?? "Failed to generate the CAIS report");
    } finally {
      setLoading(false);
    }
  }, [fetchReport, setError]);

  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const { file, rows } = await fetchReport();
      const blob = new Blob([file.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast(`CAIS file downloaded (${rows.length} records)`);
    } catch (error) {
      setError(error.message ?? "Failed to export the CAIS file");
    } finally {
      setDownloading(false);
    }
  }, [fetchReport, setError, setToast]);

  // initial load, once per mount
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { report, loading, downloading, refresh, download };
};
